import { ReactElement, useEffect, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { buildClasses } from "../utils/buildClasses";
import { keyCodes } from "../utils/KeyCodes";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { RyeIcon } from "./RyeIcon";
import {
  useFloating,
  offset,
  flip,
  shift,
  useInteractions,
  useDismiss,
  FloatingPortal,
  Placement,
  autoUpdate,
} from "@floating-ui/react";

import "overlayscrollbars/overlayscrollbars.css";

interface RyeMonthPickerProps {
  selectedDate: Dayjs | null;
  onDateChange: (date: Dayjs | null) => void;
  size?: "sm" | "md" | "lg";
  width?: "xs" | "sm" | "md" | "lg" | "full";
  variant?: "outlined" | "transparent";
  placeholder?: string;
  disabled?: boolean;
  align?: "left" | "center" | "right";
  showClearButton?: boolean;
}

export function RyeMonthPicker({
  selectedDate,
  onDateChange,
  size = "md",
  width = "md",
  variant = "outlined",
  placeholder = "MMM YYYY",
  disabled = false,
  align = "center",
  showClearButton = false,
}: RyeMonthPickerProps): ReactElement {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [originalInputValue, setOriginalInputValue] = useState<string>("");
  const [currentYear, setCurrentYear] = useState<number>(
    selectedDate?.year() || dayjs().year(),
  );
  const [highlightedMonthIndex, setHighlightedMonthIndex] = useState<
    number | null
  >(null);
  const [temporaryParsedDate, setTemporaryParsedDate] = useState<Dayjs | null>(
    null,
  );

  const inputRef = useRef<HTMLInputElement>(null);

  const placementMap: Record<string, Placement> = {
    left: "bottom-start",
    center: "bottom",
    right: "bottom-end",
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isExpanded,
    onOpenChange: (open) => {
      if (!open) close();
    },
    placement: placementMap[align] || "bottom",
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip(), shift()],
  });

  const dismiss = useDismiss(context);
  const { getFloatingProps } = useInteractions([dismiss]);

  // Update input value when selectedDate changes
  useEffect(() => {
    if (selectedDate) {
      setInputValue(selectedDate.format("MMM YYYY"));
      setCurrentYear(selectedDate.year());
      // Clear temporary parsed date since we now have a confirmed date
      setTemporaryParsedDate(null);
    } else {
      setInputValue("");
    }
  }, [selectedDate]);

  // Generate month options for the current year
  const monthOptions = Array.from({ length: 12 }, (_, index) => {
    const monthDate = dayjs().year(currentYear).month(index).startOf("month");
    return {
      index,
      date: monthDate,
      label: monthDate.format("MMM"),
      fullLabel: monthDate.format("MMMM YYYY"),
    };
  });

  function close() {
    // If the current input doesn't parse to a valid date, restore original value
    // Normalize the input for case-insensitive parsing
    const normalizedValue = inputValue
      .toLowerCase()
      .replace(/(^|\s)\w/g, (match) => match.toUpperCase());

    const parsedDate = dayjs(
      normalizedValue,
      ["MMMM YYYY", "MM/YYYY", "MMM YYYY", "MM-YYYY", "YYYY-MM"],
      true,
    );
    if (!parsedDate.isValid()) {
      setInputValue(originalInputValue);
    }

    setIsExpanded(false);
    setHighlightedMonthIndex(null);
    setTemporaryParsedDate(null);
  }

  function handleArrowMouseDown(e: React.MouseEvent<HTMLButtonElement>) {
    if (isExpanded) {
      close();
      e.preventDefault();
    } else {
      setOriginalInputValue(inputValue);
      inputRef.current?.focus();
      e.preventDefault();
    }
  }

  function selectMonth(monthIndex: number) {
    const newDate = dayjs()
      .year(currentYear)
      .month(monthIndex)
      .startOf("month");
    onDateChange(newDate);
    close();
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setInputValue(value);

    // Normalize the input for case-insensitive parsing
    // Convert to proper case (capitalize first letter of each word)
    const normalizedValue = value
      .toLowerCase()
      .replace(/(^|\s)\w/g, (match) => match.toUpperCase());

    // Try to parse the normalized input value
    const parsedDate = dayjs(
      normalizedValue,
      [
        "M YY",
        "MM YY",
        "MMM YY",
        "MMMM YY",
        "M YYYY",
        "MM YYYY",
        "MMM YYYY",
        "MMMM YYYY",
        "M/YY",
        "MM/YY",
        "MMM/YY",
        "MMMM/YY",
        "M/YYYY",
        "MM/YYYY",
        "MMM/YYYY",
        "MMMM/YYYY",
        "M-YY",
        "MM-YY",
        "MMM-YY",
        "MMMM-YY",
        "M-YYYY",
        "MM-YYYY",
        "MMM-YYYY",
        "MMMM-YYYY",
      ],
      true,
    );
    if (parsedDate.isValid()) {
      // Store the parsed date for highlighting but don't call onDateChange yet
      const monthStart = parsedDate.startOf("month");
      setTemporaryParsedDate(monthStart);
      setCurrentYear(parsedDate.year());
    } else {
      setTemporaryParsedDate(null);
    }
  }

  function handleInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.repeat) return;

    switch (e.code) {
      case keyCodes.DOWNARROW:
        e.preventDefault();
        if (!isExpanded) {
          setIsExpanded(true);
          setHighlightedMonthIndex(selectedDate?.month() || 0);
        } else {
          const nextIndex =
            highlightedMonthIndex === null
              ? 0
              : Math.min(highlightedMonthIndex + 1, monthOptions.length - 1);
          setHighlightedMonthIndex(nextIndex);
        }
        break;
      case keyCodes.UPARROW:
        e.preventDefault();
        if (isExpanded) {
          const prevIndex =
            highlightedMonthIndex === null
              ? monthOptions.length - 1
              : Math.max(highlightedMonthIndex - 1, 0);
          setHighlightedMonthIndex(prevIndex);
        }
        break;
      case keyCodes.ENTER:
        e.preventDefault();

        // Handle selection/confirmation first
        if (isExpanded && highlightedMonthIndex !== null) {
          selectMonth(highlightedMonthIndex);
        } else if (temporaryParsedDate) {
          // Confirm the temporarily parsed date
          onDateChange(temporaryParsedDate);
        }

        // Always close dropdown and unfocus input
        close();
        inputRef.current?.blur();
        break;
      case keyCodes.ESC:
        e.preventDefault();
        // Revert to original value and blur input
        setInputValue(originalInputValue);
        inputRef.current?.blur();
        close();
        break;
      case keyCodes.TAB:
        close();
        break;
    }
  }

  function handleYearNavigation(direction: "prev" | "next") {
    const newYear = direction === "prev" ? currentYear - 1 : currentYear + 1;
    setCurrentYear(newYear);
    setHighlightedMonthIndex(null);
  }

  return (
    <div
      ref={refs.setReference}
      className={buildClasses(
        {
          switch: width,
          cases: new Map([
            ["xs", "w-28"],
            ["sm", "w-44"],
            ["md", "w-52"],
            ["lg", "w-60"],
            ["full", "w-full"],
          ]),
        },
        "relative",
      )}
    >
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
        onFocus={() => {
          if (!disabled && !isExpanded) {
            setOriginalInputValue(inputValue);
            setIsExpanded(true);
          }
        }}
        onBlur={() => {
          // If we have a temporary parsed date, confirm it
          if (temporaryParsedDate) {
            onDateChange(temporaryParsedDate);
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        className={buildClasses(
          {
            switch: size,
            cases: new Map([
              ["sm", "text-sm px-2 h-7"],
              ["md", "text-md px-3 h-9"],
              ["lg", "text-lg px-4 h-11"],
            ]),
          },
          {
            if: variant === "outlined",
            then: buildClasses(
              "border-on-surface-300",
              "hover:border-on-surface-200",
              "focus:border-purple",
              "focus:shadow-purple-500/20",
            ),
            else: buildClasses(
              "border-transparent",
              "bg-transparent",
              "hover:bg-on-surface-50",
              "focus:border-purple",
              "focus:shadow-purple-500/20",
            ),
          },
          {
            if: disabled,
            then: "opacity-50 cursor-not-allowed",
          },
          "outline-none",
          "box-border",
          "w-full",
          "pr-8",
          "border",
          "text-start",
          "rounded-md",
          "focus:shadow-glow",
          "overflow-hidden",
          "whitespace-nowrap",
          "text-ellipsis",
          "hover:cursor-pointer",
          "transition-all",
          "bg-transparent",
        )}
        spellCheck="false"
      />
      {/* Close button - only show when there's a selected date and showClearButton is true */}
      {selectedDate && showClearButton && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDateChange(null);
          }}
          disabled={disabled}
          className={buildClasses(
            "flex",
            "items-center",
            "absolute",
            "right-7", // Position to the left of the dropdown arrow
            "top-1/2",
            "transform",
            "-translate-y-1/2",
            "p-1",
            "text-on-surface-500",
            "hover:text-on-surface-700",
          )}
          tabIndex={-1}
        >
          <RyeIcon name="close" size="sm" />
        </button>
      )}

      {/* Dropdown arrow */}
      <button
        type="button"
        onMouseDown={handleArrowMouseDown}
        disabled={disabled}
        className={buildClasses(
          "flex",
          "items-center",
          "absolute",
          "right-2",
          "top-1/2",
          "transform",
          "-translate-y-1/2",
          "p-1",
          "text-on-surface-500",
          "hover:text-on-surface-700",
        )}
        tabIndex={-1}
      >
        <RyeIcon name={isExpanded ? "expand_less" : "expand_more"} size="sm" />
      </button>

      {isExpanded && !disabled && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, backgroundColor: "white" }}
            {...getFloatingProps()}
            className={buildClasses(
              {
                if: width === "xs",
                then: "w-44",
                else: "w-52",
              },
              "z-[2000000000]",
              "bg-white",
              "border-tiny",
              "border-on-surface-200",
              "rounded-md",
              "shadow-popover",
              "max-h-80",
              "overflow-hidden",
            )}
          >
          {/* Year Navigation Header */}
          <div
            className={buildClasses(
              "flex",
              "items-center",
              "justify-between",
              "p-3",
              "border-b",
              "border-on-surface-200",
              "bg-surface-50",
            )}
          >
            <button
              type="button"
              onClick={() => handleYearNavigation("prev")}
              className={buildClasses(
                "flex",
                "items-center",
                "p-1",
                "hover:bg-on-surface-50",
                "rounded",
                "transition-colors",
              )}
            >
              <RyeIcon name="chevron_left" size="sm" />
            </button>
            <span
              className={buildClasses("font-medium", "text-on-surface-900")}
            >
              {currentYear}
            </span>
            <button
              type="button"
              onClick={() => handleYearNavigation("next")}
              className={buildClasses(
                "flex",
                "items-center",
                "p-1",
                "hover:bg-on-surface-50",
                "rounded",
                "transition-colors",
              )}
            >
              <RyeIcon name="chevron_right" size="sm" />
            </button>
          </div>

          {/* Months Grid */}
          <OverlayScrollbarsComponent
            options={{
              scrollbars: {
                autoHide: "scroll",
              },
            }}
            defer
            className="bg-white"
          >
            <div className="grid grid-cols-3 gap-1 p-2 bg-white">
              {monthOptions.map((month) => {
                const isSelected =
                  selectedDate?.year() === currentYear &&
                  selectedDate?.month() === month.index;
                const isHighlighted = highlightedMonthIndex === month.index;
                const isTemporaryParsed =
                  temporaryParsedDate?.year() === currentYear &&
                  temporaryParsedDate?.month() === month.index;

                return (
                  <button
                    key={month.index}
                    type="button"
                    onClick={() => selectMonth(month.index)}
                    className={buildClasses(
                      "px-3",
                      "py-2",
                      "text-sm",
                      "rounded",
                      "transition-colors",
                      "text-center",
                      "cursor-pointer",
                      {
                        if: isSelected,
                        then: buildClasses("bg-purple", "text-surface-50"),
                        elseIfs: [
                          {
                            elseIf: isHighlighted && !isSelected,
                            then: buildClasses("bg-purple", "text-surface-50"),
                          },
                          {
                            elseIf:
                              isTemporaryParsed &&
                              !isSelected &&
                              !isHighlighted,
                            then: buildClasses("bg-purple-100", "text-purple"),
                          },
                          {
                            elseIf:
                              !isSelected &&
                              !isHighlighted &&
                              !isTemporaryParsed,
                            then: buildClasses(
                              "bg-surface-50",
                              "hover:bg-on-surface-100",
                            ),
                          },
                        ],
                        else: buildClasses(
                          "bg-surface-50",
                          "hover:bg-on-surface-100",
                        ),
                      },
                    )}
                  >
                    {month.label}
                  </button>
                );
              })}
            </div>
          </OverlayScrollbarsComponent>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
}
